import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';
import { LoadableComponent } from '@loadable/component';

import { RoutesRestrictionType } from 'types/enums/routes';

import { notifyRouteLoadEvents } from 'helpers/routeEvents';
import { useMedia } from 'hooks/useMedia';
import { getAuthenticated } from 'store/auth/selectors';
import { getRedirectFromRestrictedPublicPages } from 'store/common/selectors';
import { init } from 'store/common/thunks';

import { useRestrictedRedirect } from '../../../hooks/useRestrictedRedirect';
import { FullscreenSpinner } from '../FullscreenSpinner';

interface IProps<T> extends RouteProps {
  component: React.ComponentType<T> | LoadableComponent<T>;
  componentProps?: T;
  isPrivate?: boolean;
  isRestricted?: boolean;
  isRestrictedDevice?: boolean;
  restrictedType?: RoutesRestrictionType;
}

export const AppRoute = <T extends object>({
  component: Component,
  componentProps,
  isPrivate,
  isRestricted,
  isRestrictedDevice = false,
  restrictedType,
  ...routerProps
}: IProps<T>) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isMobile, isTablet, isDesktop } = useMedia();

  const isAuthenticated = useSelector(getAuthenticated);
  const redirectFromRestrictedPublicPages = useSelector(
    getRedirectFromRestrictedPublicPages
  );

  const {
    isIntroSkipFunnelSplitActive,
    isApiBackButtonRedirected,
    redirectPathname: defaultRedirectPathname,
    redirectSearch: defaultRedirectSearch,
  } = useRestrictedRedirect({ isPrivate });

  useEffect(() => {
    if (
      (isAuthenticated && isPrivate) ||
      (!isAuthenticated && isRestricted) ||
      (!isPrivate && !isRestricted)
    ) {
      notifyRouteLoadEvents(Component, location.pathname);
    }
  }, [Component, isAuthenticated, isPrivate, isRestricted, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && isPrivate) {
      dispatch(init());
    }
  }, [dispatch, isAuthenticated, isPrivate]);

  // ? check next two redirects ( maybe there are reason to check only for isRestrictedDevice )
  if (
    isRestrictedDevice &&
    restrictedType &&
    restrictedType === RoutesRestrictionType.MobileRestricted &&
    isMobile
  ) {
    return <Redirect to="/finder" />;
  }

  if (
    isRestrictedDevice &&
    restrictedType &&
    restrictedType === RoutesRestrictionType.DesktopRestricted &&
    (isDesktop || isTablet)
  ) {
    return <Redirect to="/finder" />;
  }

  if (
    (isPrivate && !isAuthenticated) ||
    (isIntroSkipFunnelSplitActive && !isApiBackButtonRedirected)
  ) {
    return (
      <Redirect
        to={{
          pathname: defaultRedirectPathname,
          search: defaultRedirectSearch,
        }}
      />
    );
  }

  if (
    (isRestricted && isAuthenticated && redirectFromRestrictedPublicPages) ||
    (isIntroSkipFunnelSplitActive && !isApiBackButtonRedirected)
  ) {
    return (
      <Redirect
        to={{
          pathname: defaultRedirectPathname,
          search: defaultRedirectSearch,
          state: {
            isIntroSkipFunnelSplit: isIntroSkipFunnelSplitActive,
          },
        }}
      />
    );
  }

  return (
    <Route
      {...routerProps}
      render={() => {
        return (
          // @ts-ignore
          <Component {...componentProps} fallback={<FullscreenSpinner />} />
        );
      }}
    />
  );
};
